import React, { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatEUR, formatEURDecimal, formatApiError, capOptionAmount } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, X, Plus, AlertTriangle } from "lucide-react";
import FicheHeader from "./FicheHeader";
import ClientCreateForm from "./ClientCreateForm";
import SurchargesPicker from "./SurchargesPicker";
import { fleetLabelMaps, vehicleOptionLabel, categoryLabel } from "../../lib/vehicleLabel";

const toNum = (v) => { const n = parseFloat(v); return !isFinite(n) || n < 0 ? 0 : n; };

const DEFAULT_SOURCES = ["Manuel", "Téléphone", "Agence", "Walk-in", "Partenaire", "Site web"];

/** Préremplit l'état du formulaire depuis un enregistrement existant (édition) ou l'URL (création). */
function buildInitialForm({ record, isQuote, searchParams, vehicles, locations }) {
  if (record) {
    const veh = vehicles.find((v) => v.id === record.vehicle_id);
    const opts = record.options || [];
    return {
      vehicle_id: record.vehicle_id || "",
      category: veh?.category || record.vehicle_category || "",
      start_date: record.start_date || "",
      end_date: record.end_date || "",
      client_name: record.client_name || "",
      client_email: record.client_email || "",
      client_phone: record.client_phone || "",
      pickup_location_id: record.pickup_location_id || "",
      return_location_id: record.return_location_id || "",
      status: record.status || "confirmed",
      start_time: record.start_time || "",
      end_time: record.end_time || "",
      source: record.source || "Manuel",
      discount_type: record.discount_type || "",
      discount_value: record.discount_value ?? "",
      deposit_amount: record.deposit_amount ?? "",
      internal_notes: (isQuote ? record.notes : record.internal_notes) || "",
      pricing_note: record.pricing_note || "",
      valid_until: record.valid_until || "",
      options: opts.map((o) => o.key),
      optionQty: Object.fromEntries(opts.map((o) => [o.key, o.quantity || 1])),
      drivers: record.drivers || [],
    };
  }
  return {
    vehicle_id: searchParams.get("vehicle") || "",
    category: "",
    start_date: searchParams.get("date") || "",
    end_date: searchParams.get("date") || "",
    client_name: searchParams.get("client_name") || "",
    client_email: searchParams.get("client_email") || "",
    client_phone: searchParams.get("client_phone") || "",
    pickup_location_id: locations[0]?.id || "",
    return_location_id: locations[0]?.id || "",
    status: "confirmed",
    start_time: "",
    end_time: "",
    source: "Manuel",
    discount_type: "",
    discount_value: "",
    deposit_amount: "",
    internal_notes: "",
    pricing_note: "",
    valid_until: "",
    options: [],
    optionQty: {},
    drivers: [],
  };
}

/**
 * Formulaire métier unifié (pleine page) — réservations et devis, création & modification.
 * mode: "reservation" | "quote". record: entité existante (édition) ou null (création).
 */
export default function BusinessForm({
  mode = "reservation", record = null, vehicles, locations = [],
  onBack, onSaved, headerActions = null, topBanner = null, badge = null,
}) {
  const isQuote = mode === "quote";
  const isEdit = !!record;
  const [searchParams] = useSearchParams();
  const [prefs, setPrefs] = useState({ deposit_percent: 30, sources: DEFAULT_SOURCES });
  const [vatRate, setVatRate] = useState(() => (record?.vat_rate != null ? record.vat_rate : null));
  const [paidOptions, setPaidOptions] = useState([]);
  const [form, setForm] = useState(() => buildInitialForm({ record, isQuote, searchParams, vehicles, locations }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [fleetMaps, setFleetMaps] = useState({ typeMap: {}, catMap: {} });
  const [availableIds, setAvailableIds] = useState(null);
  const [availLoading, setAvailLoading] = useState(false);
  const availToken = React.useRef(0);

  // Tarification modifiable (récapitulatif) — overrides manuels
  const [priceOverride, setPriceOverride] = useState(() => (record?.custom_pricing && record.price_per_day != null ? String(record.price_per_day) : null));     // prix/jour manuel
  const [subtotalOverride, setSubtotalOverride] = useState(() => (record?.custom_pricing ? (record.subtotal ?? null) : null));
  const [optionAmtOverride, setOptionAmtOverride] = useState(() => {
    const m = {};
    for (const o of (record?.options || [])) if (o.custom_price) m[o.key] = String(o.amount ?? 0);
    return m;
  });

  // Surcoûts tarifaires appliqués (départ/retour) — décisions auto + manuelles du loueur
  const [surchargeEntries, setSurchargeEntries] = useState(() => (record?.applied_surcharges || []));
  const [transferAck, setTransferAck] = useState(() => !!record?.transfer_ack);
  const surchargesTotal = useMemo(
    () => (surchargeEntries || []).filter((e) => e.state === "applied").reduce((s, e) => s + Number(e.amount || 0), 0),
    [surchargeEntries],
  );

  // Recherche / création de client
  const [clients, setClients] = useState([]);
  const [clientQuery, setClientQuery] = useState(record?.client_name || searchParams.get("client_name") || "");
  const [clientDropdown, setClientDropdown] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [showAddClient, setShowAddClient] = useState(false);

  // Réinitialise les tarifs personnalisés quand le bien ou la période change (sauf au montage en édition).
  // Comparaison par clé précédente → robuste au double-montage de React.StrictMode.
  const prevPricingKey = React.useRef(`${form.vehicle_id}|${form.start_date}|${form.end_date}`);
  useEffect(() => {
    const key = `${form.vehicle_id}|${form.start_date}|${form.end_date}`;
    if (key === prevPricingKey.current) return;
    prevPricingKey.current = key;
    setPriceOverride(null); setSubtotalOverride(null); setOptionAmtOverride({});
  }, [form.vehicle_id, form.start_date, form.end_date]);

  // Charge la base clients du loueur pour la recherche.
  useEffect(() => { api.get("/vendor/clients").then(({ data }) => setClients(data.clients || [])).catch(() => setClients([])); }, []);

  const clientMatches = useMemo(() => {
    const q = (clientQuery || "").trim().toLowerCase();
    const base = q ? clients.filter((c) => [c.name, c.email, c.phone].filter(Boolean).some((f) => String(f).toLowerCase().includes(q))) : clients;
    return base.slice(0, 8);
  }, [clients, clientQuery]);

  const pickClient = (c) => {
    setForm((f) => ({ ...f, client_name: c.name || "", client_email: c.email || "", client_phone: c.phone || "" }));
    setClientQuery(c.name || "");
    setSelectedClientId(c.id || c.email);
    setClientDropdown(false);
  };

  const handleClientCreated = (c) => {
    if (c) { setClients((prev) => [c, ...prev.filter((x) => x.email !== c.email)]); pickClient(c); toast.success("Client créé et sélectionné"); }
    setShowAddClient(false);
  };

  // Ferme la modale « Ajouter un client » avec la touche Échap.
  useEffect(() => {
    if (!showAddClient) return;
    const onKey = (e) => { if (e.key === "Escape") setShowAddClient(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showAddClient]);


  // Recherche des biens disponibles pour la période + l'agence de départ.
  useEffect(() => {
    if (!form.start_date || !form.end_date) { setAvailableIds(null); return; }
    const token = ++availToken.current;
    setAvailLoading(true);
    api.get("/vendor/availability", {
      params: { start_date: form.start_date, end_date: form.end_date, pickup_location_id: form.pickup_location_id || undefined, exclude_booking_id: (!isQuote && record?.id) || undefined },
    })
      .then(({ data }) => { if (token === availToken.current) setAvailableIds(data.available_ids || []); })
      .catch(() => { if (token === availToken.current) setAvailableIds([]); })
      .finally(() => { if (token === availToken.current) setAvailLoading(false); });
  }, [form.start_date, form.end_date, form.pickup_location_id]);

  // Catégories et biens disponibles, dérivés de la liste de disponibilité (+ bien de l'enregistrement en édition).
  const availableVehicles = useMemo(() => {
    if (availableIds == null) return [];
    const ids = new Set(availableIds);
    if (record?.vehicle_id) ids.add(record.vehicle_id);
    return vehicles.filter((v) => ids.has(v.id));
  }, [vehicles, availableIds, record]);
  const categories = useMemo(() => {
    const map = {};
    for (const v of availableVehicles) {
      const c = v.category;
      if (!c) continue;
      if (!map[c]) map[c] = { value: c, label: categoryLabel(c, fleetMaps.catMap) || c, count: 0 };
      map[c].count++;
    }
    return Object.values(map).sort((a, b) => a.label.localeCompare(b.label));
  }, [availableVehicles, fleetMaps.catMap]);
  const vehiclesInCategory = useMemo(
    () => availableVehicles.filter((v) => v.category === form.category),
    [availableVehicles, form.category],
  );

  // Réévaluation des champs dépendants quand la disponibilité change.
  useEffect(() => {
    if (availableIds == null) return;
    const avSet = new Set(availableIds);
    if (record?.vehicle_id) avSet.add(record.vehicle_id);
    const cats = new Set(vehicles.filter((v) => avSet.has(v.id)).map((v) => v.category).filter(Boolean));
    setForm((f) => {
      let cat = f.category, veh = f.vehicle_id;
      if (cat && !cats.has(cat)) { cat = ""; veh = ""; }
      if (veh && !avSet.has(veh)) veh = "";
      return cat === f.category && veh === f.vehicle_id ? f : { ...f, category: cat, vehicle_id: veh };
    });
  }, [availableIds, vehicles, record]);

  useEffect(() => {
    api.get("/vendor/settings").then(({ data }) => {
      const bk = data.settings?.bookings || data.bookings || {};
      setPrefs((p) => ({
        deposit_percent: bk.deposit_percent ?? p.deposit_percent,
        sources: bk.sources?.length ? bk.sources : p.sources,
      }));
      setFleetMaps(fleetLabelMaps(data.fleet || data.settings?.fleet || {}));
      // TVA effective (paramètres entreprise) — lecture seule, appliquée aux devis ET réservations.
      const q = data.quotes || data.settings?.quotes || {};
      const fin = data.finance || data.settings?.finance || {};
      // Réservations : finance.vat_rate (comme les factures). Devis : logique d'héritage quotes.
      const effVat = isQuote ? (q.vat_inherit === false ? q.default_vat_rate : fin.vat_rate) : fin.vat_rate;
      if (typeof effVat === "number" && !(isEdit && record?.vat_rate != null)) setVatRate(effVat);
    }).catch(() => {});
  }, []);

  // Disponibilité produits (stock) — avertissement seulement, le loueur peut forcer
  const [availability, setAvailability] = useState({});
  useEffect(() => {
    if (!form.start_date || !form.end_date) return;
    api.get("/vendor/product-availability", {
      params: { start_date: form.start_date, end_date: form.end_date, pickup_location_id: form.pickup_location_id || undefined },
    }).then(({ data }) => setAvailability(data.availability || {})).catch(() => setAvailability({}));
  }, [form.start_date, form.end_date, form.pickup_location_id]);

  const vehicle = vehicles.find((x) => x.id === form.vehicle_id);
  const physicalLoc = vehicle?.current_location_id || vehicle?.location_id;
  const transferNeeded = !!(form.pickup_location_id && physicalLoc && physicalLoc !== form.pickup_location_id && !vehicle?.departure_locked);
  // Options filtrées pour le véhicule sélectionné (type/catégorie côté serveur)
  useEffect(() => {
    const url = form.vehicle_id ? `/vendor/booking-options?vehicle_id=${form.vehicle_id}` : "/vendor/booking-options";
    api.get(url).then(({ data }) => setPaidOptions(data.options || [])).catch(() => setPaidOptions([]));
  }, [form.vehicle_id]);

  const billableDays = useMemo(() => {
    if (!form.start_date || !form.end_date) return 0;
    const s = new Date(form.start_date), e = new Date(form.end_date);
    return Math.max(1, Math.round((e - s) / 86400000) + 1);
  }, [form.start_date, form.end_date]);

  const isMandatory = (o) => o.required && (!(o.required_categories || []).length || (o.required_categories || []).includes(vehicle?.category));
  const inDuration = (o) => (o.min_days == null || billableDays >= o.min_days) && (o.max_days == null || billableDays <= o.max_days);
  const applicableOptions = useMemo(
    () => paidOptions.filter((o) => billableDays === 0 || inDuration(o)),
    [paidOptions, billableDays, vehicle],
  );
  const optQty = (o) => (isMandatory(o) ? 1 : (form.optionQty[o.key] || 1));
  const isSelected = (o) => isMandatory(o) || form.options.includes(o.key);
  // Tarif issu EXCLUSIVEMENT de la grille tarifaire (catégorie × saison × durée).
  const [grid, setGrid] = useState(null); // { resolved, daily_price, total, multi_season, breakdown } | null
  const [gridLoading, setGridLoading] = useState(false);
  const [calcMethod, setCalcMethod] = useState("per_day");
  useEffect(() => {
    if (!vehicle?.category || !form.start_date || !form.end_date) { setGrid(null); return; }
    const s = new Date(form.start_date), e = new Date(form.end_date);
    const days = Math.max(1, Math.round((e - s) / 86400000) + 1);
    setGridLoading(true);
    api.get("/vendor/pricing/resolve", { params: { category: vehicle.category, start_date: form.start_date, days, method: calcMethod } })
      .then(({ data }) => setGrid(data))
      .catch(() => setGrid({ resolved: false, daily_price: null }))
      .finally(() => setGridLoading(false));
  }, [vehicle?.category, form.start_date, form.end_date, calcMethod]);

  const preview = useMemo(() => {
    if (!vehicle || !form.start_date || !form.end_date) return null;
    const s = new Date(form.start_date), e = new Date(form.end_date);
    const days = Math.max(1, Math.round((e - s) / 86400000) + 1);
    const gridPpd = grid?.resolved ? Number(grid.daily_price) : null;
    const gridSubtotal = grid?.resolved ? Number(grid.total) : 0;
    const ppd = priceOverride !== null ? toNum(priceOverride) : gridPpd;
    const computedSubtotal = priceOverride !== null ? (ppd != null ? Math.round(ppd * days * 100) / 100 : 0) : gridSubtotal;
    const subtotal = subtotalOverride !== null ? toNum(subtotalOverride) : computedSubtotal;
    const optionsDetail = applicableOptions.filter((o) => isSelected(o)).map((o) => {
      const qty = optQty(o);
      let unit = o.per_day ? Math.round(o.price * days * 100) / 100 : o.price;
      if (o.per_day && o.price_cap != null && o.price_cap > 0) unit = Math.min(unit, o.price_cap);
      const computedAmount = Math.round(unit * qty * 100) / 100;
      const ov = optionAmtOverride[o.key];
      const amount = ov !== undefined ? toNum(ov) : computedAmount;
      return { key: o.key, label: o.label + (qty > 1 ? ` ×${qty}` : "") + (isMandatory(o) ? " (obligatoire)" : ""), amount, computed: computedAmount, quantity: qty };
    });
    const optionsTotal = Math.round(optionsDetail.reduce((sum, o) => sum + o.amount, 0) * 100) / 100;
    const catalogOptionsTotal = Math.round(optionsDetail.reduce((sum, o) => sum + o.computed, 0) * 100) / 100;
    const base = Math.round((subtotal + optionsTotal) * 100) / 100;
    const dv = parseFloat(form.discount_value);
    let discount = 0;
    if (form.discount_type && dv > 0) {
      discount = form.discount_type === "percent" ? (base * Math.min(dv, 100)) / 100 : Math.min(dv, base);
    }
    // TTC = prix de référence (grilles/saisies en TTC). Surcoûts (TTC) ajoutés au total. HT et TVA dérivés du TTC.
    const surcharges = Math.round((surchargesTotal || 0) * 100) / 100;
    // Frais d'aller simple (TTC) de l'agence de départ, une seule fois si retour dans une autre agence.
    const pickupLoc = (locations || []).find((l) => l.id === form.pickup_location_id);
    const ow = pickupLoc?.settings?.one_way || {};
    const retDiff = form.return_location_id && form.return_location_id !== form.pickup_location_id;
    const oneWayFee = (retDiff && ow.enabled) ? Math.round((Number(ow.fee) || 0) * 100) / 100 : 0;
    const total = Math.round((base - discount + surcharges + oneWayFee) * 100) / 100;
    const totalTTC = total;
    const totalHT = vatRate ? Math.round((total / (1 + vatRate / 100)) * 100) / 100 : total;
    const vat = Math.round((totalTTC - totalHT) * 100) / 100;
    const deposit = form.deposit_amount !== "" ? parseFloat(form.deposit_amount) || 0 : Math.round(total * (prefs.deposit_percent / 100) * 100) / 100;
    // Écart tarif grille vs tarif personnalisé (hors remise explicite) : réf. grille − montant facturé.
    const gridReference = grid?.resolved ? Math.round((gridSubtotal + catalogOptionsTotal) * 100) / 100 : null;
    const ecart = gridReference != null ? Math.round((gridReference - base) * 100) / 100 : null;
    return { days, ppd, subtotal, optionsDetail, optionsTotal, discount, surcharges, oneWayFee, total, totalHT, vat, totalTTC, deposit, gridReference, ecart, unconfigured: ppd == null };
  }, [vehicle, form.start_date, form.end_date, form.discount_type, form.discount_value, form.deposit_amount, form.options, form.optionQty, form.pickup_location_id, form.return_location_id, locations, applicableOptions, prefs.deposit_percent, grid, priceOverride, subtotalOverride, optionAmtOverride, isQuote, vatRate, surchargesTotal]);

  const save = async () => {
    setError("");
    if (!form.start_date || !form.end_date) { setError("Renseignez la période (dates de départ et de retour)."); return; }
    if (locations.length > 0 && (!form.pickup_location_id || !form.return_location_id)) { setError("Sélectionnez l'agence de départ et l'agence de retour."); return; }
    if (!form.category) { setError("Sélectionnez une catégorie de bien."); return; }
    if (!form.vehicle_id) { setError("Sélectionnez un bien."); return; }
    if (!form.client_name || !form.client_email) { setError("Complétez les informations du client."); return; }
    // Les heures sont obligatoires pour une réservation ; optionnelles pour un devis (fixées à la conversion).
    if (!isQuote) {
      const fe = {};
      if (!(form.start_time || "").trim()) fe.start_time = "L'heure de départ est obligatoire.";
      if (!(form.end_time || "").trim()) fe.end_time = "L'heure de retour est obligatoire.";
      if (Object.keys(fe).length) {
        setFieldErrors(fe);
        setError("Renseignez l'heure de départ et l'heure de retour.");
        return;
      }
    }
    setFieldErrors({});
    if (preview?.unconfigured) {
      setError("Tarif non configuré pour cette catégorie, saison et durée. Complétez la grille tarifaire (Paramétrages → Tarification) avant de continuer.");
      return;
    }
    setSaving(true);
    try {
      const selectedOpts = (preview?.optionsDetail || []).map((d) => (
        optionAmtOverride[d.key] !== undefined
          ? { key: d.key, quantity: d.quantity, amount: d.amount }
          : { key: d.key, quantity: d.quantity }
      ));
      const common = {
        vehicle_id: form.vehicle_id,
        start_date: form.start_date,
        end_date: form.end_date,
        start_time: (form.start_time || "").trim(),
        end_time: (form.end_time || "").trim(),
        pickup_location_id: form.pickup_location_id || null,
        return_location_id: form.return_location_id || null,
        client_name: form.client_name,
        client_email: form.client_email,
        client_phone: form.client_phone,
        pricing_note: form.pricing_note?.trim() ? form.pricing_note.trim() : null,
        options: selectedOpts,
        discount_type: form.discount_type || null,
        discount_value: form.discount_value === "" ? null : parseFloat(form.discount_value),
        surcharges: (surchargeEntries || []).map((e) => ({ surcharge_id: e.surcharge_id, event: e.event, origin: e.origin, state: e.state })),
        transfer_ack: transferAck,
      };

      if (isQuote) {
        const payload = {
          ...common,
          notes: form.internal_notes || "",
          price_per_day: preview?.ppd ?? 0,
          subtotal: preview?.subtotal ?? null,
          valid_until: form.valid_until || null,
        };
        if (!payload.valid_until) delete payload.valid_until;
        const { data } = isEdit
          ? await api.patch(`/vendor/quotes/${record.id}`, payload)
          : await api.post("/vendor/quotes", payload);
        toast.success(isEdit ? "Devis mis à jour" : "Devis créé");
        onSaved(data.quote);
      } else {
        const payload = {
          ...common,
          source: form.source,
          internal_notes: form.internal_notes,
          status: form.status,
          price_per_day: priceOverride !== null ? (preview?.ppd ?? null) : null,
          subtotal: subtotalOverride !== null ? (preview?.subtotal ?? null) : null,
          calc_method: calcMethod,
          deposit_amount: form.deposit_amount === "" ? null : parseFloat(form.deposit_amount),
          drivers: form.drivers,
        };
        if (isEdit) {
          await api.patch(`/vendor/bookings/${record.id}`, payload);
          toast.success("Réservation mise à jour");
          onSaved({ ...record, ...payload });
        } else {
          const { data } = await api.post("/vendor/bookings", payload);
          toast.success("Réservation créée");
          onSaved(data.booking);
        }
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Erreur");
    }
    setSaving(false);
  };

  const titleText = isQuote
    ? (isEdit ? `Devis ${record.reference || ""}`.trim() : "Nouveau devis")
    : (isEdit ? `Réservation ${record.reference || ""}`.trim() : "Nouvelle réservation");
  const subtitleText = isQuote
    ? (isEdit ? "Modifier le devis" : "Établir un devis (client, véhicule, dates)")
    : (isEdit ? "Modifier la réservation" : "Enregistrez une location (sans paiement Stripe)");

  return (
    <div className="h-full overflow-y-auto p-4 sm:p-6" data-testid={isQuote ? "quote-editor" : "booking-editor"}>
      <div className="w-full">
        <FicheHeader
          onBack={onBack}
          backLabel={isQuote ? "Retour aux devis" : "Retour aux réservations"}
          title={titleText}
          subtitle={subtitleText}
          badge={badge}
          editing
          onCancel={onBack}
          onSave={save}
          saving={saving}
          actions={headerActions}
        />

        {topBanner}

        <div className="grid lg:grid-cols-3 gap-4 sm:gap-6 mt-6">
          <div className="lg:col-span-2 space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6">
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-400 mb-4">Période, agence & bien</div>
              <div className="space-y-4">
                {/* Étape 1 — Période */}
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Date de départ" type="date" value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} required testid="pilotage-booking-start" />
                  <Field label="Heure de départ" type="time" value={form.start_time} onChange={(v) => { setForm({ ...form, start_time: v }); if (fieldErrors.start_time) setFieldErrors((e) => ({ ...e, start_time: undefined })); }} required={!isQuote} error={fieldErrors.start_time} testid="pilotage-booking-start-time" />
                  <Field label="Date de retour" type="date" value={form.end_date} onChange={(v) => setForm({ ...form, end_date: v })} required testid="pilotage-booking-end" />
                  <Field label="Heure de retour" type="time" value={form.end_time} onChange={(v) => { setForm({ ...form, end_time: v }); if (fieldErrors.end_time) setFieldErrors((e) => ({ ...e, end_time: undefined })); }} required={!isQuote} error={fieldErrors.end_time} testid="pilotage-booking-end-time" />
                </div>

                {/* Étape 2 — Agences */}
                {locations.length > 0 && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Agence de départ<span className="text-red-500 ml-0.5">*</span></label>
                      <select value={form.pickup_location_id} onChange={(e) => setForm({ ...form, pickup_location_id: e.target.value })} required className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:ring-2 focus:ring-violet-600 outline-none bg-white" data-testid="pilotage-booking-pickup">
                        <option value="">Sélectionner</option>
                        {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Agence de retour<span className="text-red-500 ml-0.5">*</span></label>
                      <select value={form.return_location_id} onChange={(e) => setForm({ ...form, return_location_id: e.target.value })} required className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:ring-2 focus:ring-violet-600 outline-none bg-white" data-testid="pilotage-booking-return">
                        <option value="">Sélectionner</option>
                        {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                      </select>
                    </div>
                  </div>
                )}

                {/* Étape 3 — Catégorie */}
                <div>
                  <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Catégorie du bien<span className="text-red-500 ml-0.5">*</span></label>
                  <select
                    value={form.category}
                    onChange={(e) => { const c = e.target.value; setForm((f) => ({ ...f, category: c, vehicle_id: availableVehicles.some((v) => v.id === f.vehicle_id && v.category === c) ? f.vehicle_id : "" })); }}
                    disabled={!form.start_date || !form.end_date || availLoading || categories.length === 0}
                    className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:ring-2 focus:ring-violet-600 outline-none bg-white disabled:bg-slate-50 disabled:text-slate-400"
                    data-testid="pilotage-booking-category"
                  >
                    <option value="">{availLoading ? "Recherche des disponibilités…" : "Sélectionner"}</option>
                    {categories.map((c) => <option key={c.value} value={c.value}>{c.label} ({c.count})</option>)}
                  </select>
                  {form.start_date && form.end_date && !availLoading && categories.length === 0 && (
                    <p className="mt-1.5 text-xs text-amber-600" data-testid="pilotage-booking-no-availability">
                      Aucun bien disponible pour cette période et cette agence. Modifiez vos critères.
                    </p>
                  )}
                </div>

                {/* Étape 4 — Bien */}
                <div>
                  <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Bien à réserver<span className="text-red-500 ml-0.5">*</span></label>
                  <select
                    value={form.vehicle_id}
                    onChange={(e) => setForm({ ...form, vehicle_id: e.target.value })}
                    required
                    disabled={!form.category || vehiclesInCategory.length === 0}
                    className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:ring-2 focus:ring-violet-600 outline-none bg-white disabled:bg-slate-50 disabled:text-slate-400"
                    data-testid="pilotage-booking-vehicle"
                  >
                    <option value="">{!form.category ? "Choisissez d'abord une catégorie" : "Sélectionner"}</option>
                    {vehiclesInCategory.map((v) => <option key={v.id} value={v.id}>{vehicleOptionLabel(v, fleetMaps.typeMap, fleetMaps.catMap)}</option>)}
                  </select>
                  {vehicles.length === 0 && (
                    <p className="mt-1.5 text-xs text-amber-600" data-testid="pilotage-booking-vehicle-empty">
                      Aucun véhicule dans votre flotte. Ajoutez un véhicule pour créer une réservation.
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6">
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-400 mb-4">Client</div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="relative" data-testid="booking-client-search-wrap">
                  <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Nom du client<span className="text-red-500 ml-0.5">*</span></label>
                  <input
                    type="text"
                    value={clientQuery}
                    onChange={(e) => { const v = e.target.value; setClientQuery(v); setForm((f) => ({ ...f, client_name: v })); setSelectedClientId(null); setClientDropdown(true); }}
                    onFocus={() => setClientDropdown(true)}
                    onBlur={() => setTimeout(() => setClientDropdown(false), 150)}
                    placeholder="Rechercher ou saisir un nom…"
                    autoComplete="off"
                    required
                    className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:ring-2 focus:ring-violet-600 outline-none"
                    data-testid="pilotage-booking-client-name"
                  />
                  {clientDropdown && (
                    <div className="absolute z-30 mt-1 w-full rounded-xl border border-slate-200 bg-white shadow-lg max-h-64 overflow-y-auto" data-testid="booking-client-dropdown">
                      {clientMatches.length > 0 ? clientMatches.map((c) => (
                        <button type="button" key={c.id || c.email} onMouseDown={(e) => e.preventDefault()} onClick={() => pickClient(c)} className="w-full text-left px-3 py-2 hover:bg-violet-50 flex items-center gap-2" data-testid={`booking-client-option-${c.email}`}>
                          <div className="h-7 w-7 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center text-xs font-semibold shrink-0">{c.name?.[0]?.toUpperCase() || "?"}</div>
                          <div className="min-w-0">
                            <div className="text-sm text-slate-800 truncate">{c.name}</div>
                            <div className="text-[11px] text-slate-400 truncate">{c.email}{c.phone ? ` · ${c.phone}` : ""}</div>
                          </div>
                        </button>
                      )) : (
                        <div className="px-3 py-2 text-xs text-slate-400" data-testid="booking-client-no-match">Aucun client existant{clientQuery.trim() ? ` pour « ${clientQuery.trim()} »` : ""}.</div>
                      )}
                      <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => { setClientDropdown(false); setShowAddClient(true); }} className="w-full text-left px-3 py-2.5 border-t border-slate-100 text-sm font-semibold text-violet-600 hover:bg-violet-50 flex items-center gap-1.5" data-testid="booking-add-client-btn">
                        <Plus className="h-4 w-4" /> Ajouter un client
                      </button>
                    </div>
                  )}
                </div>
                <Field label="Email" type="email" value={form.client_email} onChange={(v) => setForm({ ...form, client_email: v })} required testid="pilotage-booking-client-email" />
                <Field label="Téléphone" value={form.client_phone} onChange={(v) => setForm({ ...form, client_phone: v })} testid="pilotage-booking-client-phone" />
                {!isQuote ? (
                  <div>
                    <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Source</label>
                    <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:ring-2 focus:ring-violet-600 outline-none bg-white" data-testid="pilotage-booking-source">
                      {prefs.sources.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                ) : null}
              </div>
            </div>

            {!isQuote && (
            <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="booking-drivers-card">
              <div className="flex items-center justify-between mb-4">
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-400">Conducteurs supplémentaires</div>
                {form.drivers.length < 5 && (
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, drivers: [...form.drivers, { name: "", license_number: "", phone: "" }] })}
                    className="text-xs font-semibold text-violet-600 hover:text-violet-700"
                    data-testid="booking-add-driver-btn"
                  >
                    + Ajouter un conducteur
                  </button>
                )}
              </div>
              {form.drivers.length === 0 ? (
                <p className="text-xs text-slate-400">Le client est le conducteur principal. Ajoutez ici les conducteurs additionnels (repris sur le contrat).</p>
              ) : (
                <div className="space-y-3">
                  {form.drivers.map((d, i) => (
                    <div key={i} className="grid sm:grid-cols-[1fr_1fr_1fr_auto] gap-3 items-end" data-testid={`booking-driver-row-${i}`}>
                      <Field label="Nom" value={d.name} onChange={(v) => setForm({ ...form, drivers: form.drivers.map((x, j) => j === i ? { ...x, name: v } : x) })} testid={`booking-driver-name-${i}`} />
                      <Field label="N° de permis" value={d.license_number} onChange={(v) => setForm({ ...form, drivers: form.drivers.map((x, j) => j === i ? { ...x, license_number: v } : x) })} testid={`booking-driver-license-${i}`} />
                      <Field label="Téléphone" value={d.phone} onChange={(v) => setForm({ ...form, drivers: form.drivers.map((x, j) => j === i ? { ...x, phone: v } : x) })} testid={`booking-driver-phone-${i}`} />
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, drivers: form.drivers.filter((_, j) => j !== i) })}
                        className="h-10 px-3 rounded-xl border border-red-200 text-red-500 hover:bg-red-50 text-xs font-semibold"
                        data-testid={`booking-driver-remove-${i}`}
                      >
                        Retirer
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            )}

            {applicableOptions.length > 0 && (
              <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="booking-options-card">
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-400 mb-4">Options</div>
                <div className="space-y-2">
                  {applicableOptions.map((o) => {
                    const av = availability[o.key];
                    const soldOut = av && av.remaining <= 0;
                    const mandatory = isMandatory(o);
                    const selected = isSelected(o);
                    const qty = optQty(o);
                    const maxQty = Math.min(o.max_per_booking || 99, av ? Math.max(1, av.remaining) : 99);
                    const setQty = (q) => setForm({ ...form, optionQty: { ...form.optionQty, [o.key]: Math.max(1, Math.min(maxQty, q)) } });
                    return (
                      <div key={o.key} className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${mandatory ? "border-violet-300 bg-violet-50/40" : soldOut ? "border-amber-300 bg-amber-50/50" : "border-slate-200"}`} data-testid={`booking-option-${o.key}`}>
                        <input
                          type="checkbox"
                          checked={selected}
                          disabled={mandatory}
                          onChange={(e) => setForm({ ...form, options: e.target.checked ? [...form.options, o.key] : form.options.filter((k) => k !== o.key) })}
                          className="h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500 disabled:opacity-60"
                          data-testid={`booking-option-check-${o.key}`}
                        />
                        <span className="flex-1 text-sm text-slate-700">
                          {o.label}
                          {mandatory && <span className="ml-2 px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 text-[10px] font-bold" data-testid={`booking-option-mandatory-${o.key}`}>Obligatoire</span>}
                          {soldOut && <span className="ml-2 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold" data-testid={`booking-option-soldout-${o.key}`}>⚠ Épuisé ({av.used}/{av.limit})</span>}
                          {av && !soldOut && <span className="ml-2 text-[10px] text-slate-400">{av.remaining} dispo</span>}
                        </span>
                        {selected && (o.max_per_booking == null || o.max_per_booking > 1) && !mandatory && (
                          <div className="inline-flex items-center gap-1.5" data-testid={`booking-option-qty-${o.key}`}>
                            <button type="button" onClick={() => setQty(qty - 1)} className="h-6 w-6 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50">−</button>
                            <span className="text-xs font-bold w-5 text-center">{qty}</span>
                            <button type="button" onClick={() => setQty(qty + 1)} className="h-6 w-6 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50">+</button>
                          </div>
                        )}
                        <span className="text-xs font-semibold text-slate-900">{formatEUR(o.price)}{o.per_day ? " / jour" : ""}{o.price_cap != null ? ` · max ${formatEUR(o.price_cap)}` : ""}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6">
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-400 mb-4">{isQuote ? "Remise & validité" : "Remise & acompte"}</div>
              <div className="grid sm:grid-cols-3 gap-4">
                <div>
                  <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Remise</label>
                  <select value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })} className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:ring-2 focus:ring-violet-600 outline-none bg-white" data-testid="pilotage-booking-discount-type">
                    <option value="">Aucune</option>
                    <option value="percent">Pourcentage (%)</option>
                    <option value="amount">Montant (€)</option>
                  </select>
                </div>
                {form.discount_type && (
                  <Field label={form.discount_type === "percent" ? "Remise (%)" : "Remise (€)"} type="number" value={form.discount_value} onChange={(v) => setForm({ ...form, discount_value: v })} testid="pilotage-booking-discount-value" />
                )}
                {isQuote ? (
                  <Field label="Date de validité" type="date" value={form.valid_until} onChange={(v) => setForm({ ...form, valid_until: v })} testid="quote-valid-until" />
                ) : (
                  <Field label={`Acompte (défaut ${prefs.deposit_percent}%)`} type="number" value={form.deposit_amount} onChange={(v) => setForm({ ...form, deposit_amount: v })} placeholder={preview ? String(preview.deposit) : ""} testid="pilotage-booking-deposit" />
                )}
              </div>
              {isQuote && !isEdit && !form.valid_until && (
                <p className="mt-2 text-[10px] text-slate-400" data-testid="quote-validity-hint">Validité par défaut appliquée automatiquement (Paramétrages → Devis).</p>
              )}
              <div className="mt-4">
                <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Notes internes</label>
                <textarea value={form.internal_notes} onChange={(e) => setForm({ ...form, internal_notes: e.target.value })} rows={2} placeholder="Visible uniquement par vous" className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:ring-2 focus:ring-violet-600 outline-none resize-none" data-testid="pilotage-booking-notes" />
              </div>
              {(priceOverride !== null || subtotalOverride !== null || Object.keys(optionAmtOverride).length > 0) && (
                <div className="mt-4" data-testid="pilotage-booking-pricing-note-block">
                  <label className="text-xs tracking-[0.2em] uppercase font-bold text-violet-500">Motif du tarif personnalisé <span className="text-slate-400 normal-case tracking-normal font-normal">(optionnel)</span></label>
                  <textarea value={form.pricing_note} onChange={(e) => setForm({ ...form, pricing_note: e.target.value })} rows={2} placeholder="Ex : client fidèle, geste commercial, partenaire…" className="mt-1.5 w-full rounded-xl border border-violet-200 bg-violet-50/40 px-4 py-2.5 text-sm focus:ring-2 focus:ring-violet-600 outline-none resize-none" data-testid="pilotage-booking-pricing-note" />
                </div>
              )}
            </div>

            {error && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3" data-testid="booking-editor-error">{error}</div>}
          </div>

          <div className="lg:sticky lg:top-6 self-start">
            <div className="rounded-2xl border border-violet-100 bg-violet-50/60 p-5" data-testid="booking-editor-preview">
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-violet-500 mb-3">Récapitulatif</div>
              {preview ? (
                preview.unconfigured ? (
                  <div className="text-xs text-amber-700 font-medium bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="booking-editor-unconfigured">
                    Tarif non configuré{categoryLabel(vehicle.category, fleetMaps.catMap) ? ` pour la catégorie « ${categoryLabel(vehicle.category, fleetMaps.catMap)} »` : ""} sur cette saison et durée. Complétez la grille tarifaire (Paramétrages → Tarification) pour créer cette réservation.
                  </div>
                ) : (
                <div className="space-y-2 text-sm">
                  <Row label="Véhicule" val={`${vehicle.brand} ${vehicle.model}`} />
                  <Row label="Durée" val={`${preview.days} jour${preview.days > 1 ? "s" : ""}`} />
                  {grid?.multi_season && (
                    <div className="rounded-lg bg-amber-50 border border-amber-200 p-2.5 my-1" data-testid="booking-multi-season">
                      <div className="text-[11px] font-semibold text-amber-800 mb-1.5">Cette réservation traverse plusieurs saisons tarifaires. Choisissez la méthode de calcul.</div>
                      <div className="inline-flex rounded-lg border border-amber-300 overflow-hidden bg-white">
                        <button type="button" onClick={() => setCalcMethod("per_day")} className={`px-2 py-1 text-[10px] font-semibold ${calcMethod === "per_day" ? "bg-amber-500 text-white" : "text-amber-700"}`} data-testid="booking-method-per_day">Jour par jour</button>
                        <button type="button" onClick={() => setCalcMethod("departure")} className={`px-2 py-1 text-[10px] font-semibold ${calcMethod === "departure" ? "bg-amber-500 text-white" : "text-amber-700"}`} data-testid="booking-method-departure">Saison du départ</button>
                      </div>
                      {(grid.breakdown || []).map((b, i) => (
                        <div key={i} className="flex justify-between text-[11px] text-amber-800 mt-1"><span>{b.season_label} · {b.days}j × {b.daily_price}€</span><span>{b.subtotal}€</span></div>
                      ))}
                    </div>
                  )}
                  <EditableRow label="Prix / jour" testid="booking-edit-ppd" value={priceOverride !== null ? priceOverride : (preview.ppd ?? "")} onChange={(v) => { setPriceOverride(v); setSubtotalOverride(null); }} />
                  <EditableRow label="Sous-total location" testid="booking-edit-subtotal" value={subtotalOverride !== null ? subtotalOverride : preview.subtotal} onChange={(v) => setSubtotalOverride(v)} />
                  {grid?.resolved && (priceOverride !== null || subtotalOverride !== null) && (
                    <div className="flex justify-end -mt-1">
                      <button type="button" onClick={() => { setPriceOverride(null); setSubtotalOverride(null); }} className="text-[11px] font-medium text-violet-600 hover:text-violet-700 hover:underline" data-testid="booking-reset-grid-price">Rétablir le tarif grille</button>
                    </div>
                  )}
                  {preview.optionsDetail.map((o) => {
                    const offert = optionAmtOverride[o.key] !== undefined && toNum(optionAmtOverride[o.key]) === 0;
                    return (
                      <EditableRow
                        key={o.key}
                        label={
                          <span className="inline-flex items-center gap-1.5 min-w-0">
                            <span className="truncate">{o.label}</span>
                            {offert && (
                              <span className="shrink-0 px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold uppercase tracking-wide" data-testid={`booking-option-offert-badge-${o.key}`}>Offert</span>
                            )}
                          </span>
                        }
                        testid={`booking-edit-option-${o.key}`}
                        prefix="+"
                        value={optionAmtOverride[o.key] !== undefined ? optionAmtOverride[o.key] : o.amount}
                        onChange={(v) => setOptionAmtOverride((m) => ({ ...m, [o.key]: v }))}
                        action={
                          <button
                            type="button"
                            onClick={() => setOptionAmtOverride((m) => { if (offert) { const n = { ...m }; delete n[o.key]; return n; } return { ...m, [o.key]: "0" }; })}
                            className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-md transition-colors ${offert ? "bg-emerald-100 text-emerald-700" : "text-slate-400 hover:bg-slate-100 hover:text-slate-600"}`}
                            data-testid={`booking-option-offert-${o.key}`}
                            title={offert ? "Rétablir le tarif de l'option" : "Offrir cette option (0 €)"}
                          >
                            {offert ? "Offert ✓" : "Offert"}
                          </button>
                        }
                      />
                    );
                  })}
                  {preview.discount > 0 && <Row label="Remise" val={`− ${formatEURDecimal(preview.discount)}`} />}
                  <SurchargesPicker
                    context={{
                      pickup_location_id: form.pickup_location_id,
                      return_location_id: form.return_location_id,
                      start_date: form.start_date,
                      start_time: form.start_time,
                      end_date: form.end_date,
                      end_time: form.end_time,
                    }}
                    entries={surchargeEntries}
                    onChange={setSurchargeEntries}
                  />
                  {preview.oneWayFee > 0 && (
                    <Row label="Frais d'aller simple" val={`+ ${formatEURDecimal(preview.oneWayFee)}`} testid="recap-oneway-fee" />
                  )}
                  {!isQuote && transferNeeded && (
                    <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 p-2.5" data-testid="transfer-alert">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                        <div className="text-[12px] text-amber-800">
                          Transfert nécessaire : ce véhicule n'est pas actuellement dans l'agence de départ sélectionnée.
                          <label className="mt-1.5 flex items-center gap-2 cursor-pointer" data-testid="transfer-ack-label">
                            <input type="checkbox" checked={transferAck} onChange={(e) => setTransferAck(e.target.checked)} className="accent-amber-600" data-testid="transfer-ack-checkbox" />
                            <span className="font-semibold">Je prends en charge l'organisation du transfert</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  )}
                  {preview.ecart != null && Math.abs(preview.ecart) >= 0.01 && (
                    <div className="flex items-center justify-between text-[12px]" data-testid="booking-pricing-gap">
                      <span className="text-slate-500">{preview.ecart > 0 ? "Remise vs tarif grille" : "Majoration vs tarif grille"}</span>
                      <span className={`font-semibold tabular-nums ${preview.ecart > 0 ? "text-emerald-600" : "text-amber-600"}`}>
                        {preview.ecart > 0 ? "− " : "+ "}{formatEURDecimal(Math.abs(preview.ecart))}
                      </span>
                    </div>
                  )}
                  {vatRate != null && vatRate > 0 && (
                    <>
                      <Row label="Total HT" val={formatEURDecimal(preview.totalHT)} testid="recap-ht" />
                      <Row label={`TVA incluse (${vatRate}%)`} val={formatEURDecimal(preview.vat)} testid="recap-vat" />
                    </>
                  )}
                  <Row label="Total TTC" val={formatEURDecimal(preview.totalTTC)} strong testid="recap-ttc" />
                  {!isQuote && (
                    <Row label="Acompte suggéré" val={formatEURDecimal(preview.deposit)} />
                  )}
                </div>
                )
              ) : (
                <div className="text-xs text-slate-500">{gridLoading ? "Calcul du tarif…" : "Sélectionnez le véhicule et les dates pour voir le total."}</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showAddClient && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          data-testid="booking-add-client-modal"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setShowAddClient(false); }}
        >
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-5 sm:p-6" role="dialog" aria-modal="true" aria-label="Nouveau client">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-medium text-slate-900">Nouveau client</h3>
              <button type="button" onClick={() => setShowAddClient(false)} className="text-slate-400 hover:text-slate-700" data-testid="booking-add-client-close"><X className="h-5 w-5" /></button>
            </div>
            <ClientCreateForm defaultStatus="client" defaultName={clientQuery} onCreated={handleClientCreated} onCancel={() => setShowAddClient(false)} />
          </div>
        </div>
      )}
    </div>
  );
}

function EditableRow({ label, value, onChange, testid, prefix, action }) {
  return (
    <div className="flex items-center justify-between text-slate-600 gap-2">
      <span className="min-w-0 truncate">{label}</span>
      <div className="inline-flex items-center gap-1.5 shrink-0">
        {action}
        {prefix && <span className="text-slate-400">{prefix}</span>}
        <div className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-0.5 focus-within:ring-2 focus-within:ring-violet-500 focus-within:border-violet-500">
          <input type="number" min="0" step="0.01" inputMode="decimal" value={value} onChange={(e) => onChange(e.target.value)} onFocus={(e) => e.target.select()} className="w-16 text-right text-sm outline-none bg-transparent tabular-nums" data-testid={testid} />
          <span className="text-xs text-slate-400">€</span>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", testid, required, error }) {
  return (
    <div>
      <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">{label}{required && <span className="text-red-500 ml-0.5">*</span>}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} required={required} className={`mt-1.5 w-full rounded-xl border px-4 py-2.5 text-sm focus:ring-2 focus:ring-violet-600 outline-none ${error ? "border-red-300" : "border-slate-200"}`} data-testid={testid} />
      {error && <p className="mt-1 text-xs text-red-600" data-testid={testid ? `${testid}-error` : undefined}>{error}</p>}
    </div>
  );
}

function Row({ label, val, strong, testid }) {
  return (
    <div className={`flex items-center justify-between ${strong ? "pt-2 border-t border-violet-200 font-semibold text-slate-900" : "text-slate-600"}`} data-testid={testid}>
      <span>{label}</span>
      <span className="text-right">{val}</span>
    </div>
  );
}
